import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, mergeMap, Observable, of, throwError } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, ContractHelper, PortfolioService } from '@ecm/portfolio';

import { ErrorPageService } from '../../services/error-page.service';
import { RouterService } from '../../services/router.service';
import { AbstractPortfolioResolver } from './shared/abstract-portfolio.resolver';

@Injectable({
    providedIn: 'root',
})
export class ContractPortfolioResolver extends AbstractPortfolioResolver {
    /**
     * Contract selector routing path
     * @type {string}
     */
    public static readonly CONTRACT_SELECTOR_PATH: string = 'selectionner-un-contrat';

    public constructor(
        protected errorPageService: ErrorPageService,
        protected portfolioService: PortfolioService,
        protected routerService: RouterService,
    ) {
        super(errorPageService, portfolioService, routerService);
    }

    /**
     * Checks cid/portfolio coherence & contract eligibility with the filters.
     * If no (valid) contract is provided in the URL, the user will be redirected to the ContractSelector component.
     * @param {ActivatedRouteSnapshot} activatedRouteSnapshot
     * @param {RouterStateSnapshot} routerStateSnapshot
     * @returns {Observable<boolean>}
     */
    public resolve(
        activatedRouteSnapshot: ActivatedRouteSnapshot,
        routerStateSnapshot: RouterStateSnapshot,
    ): Observable<boolean> {
        const filterParams = activatedRouteSnapshot.data;
        const filter = filterParams ? filterParams.filter || filterParams.verticale : null;
        return this.checkContract(activatedRouteSnapshot, routerStateSnapshot, filter).pipe(
            mergeMap(() => {
                const routeContractId: string = activatedRouteSnapshot.params.cid;
                return this.portfolioService.getContractByCid(routeContractId, filter).pipe(
                    mergeMap((contract: Contract) => {
                        const contractWithUniverse = ContractHelper.computeUniverse(contract);
                        if (ContractHelper.isAuthorized(contractWithUniverse, filterParams)) {
                            EcmDispatcherModule.sendConsoleInfo(
                                ContractPortfolioResolver.name,
                                `Contract ${routeContractId} is allowed, will let go to route ${routerStateSnapshot.url}`,
                            );
                            return of(true);
                        }
                        this.force = true;
                        return throwError(
                            () => `Contract ${routeContractId} is not allowed for route ${routerStateSnapshot.url}`,
                        );
                    }),
                );
            }),
            catchError((error: any) => {
                EcmDispatcherModule.sendConsoleError(ContractPortfolioResolver.name, error);
                let updatedReturnUrl = routerStateSnapshot.url;

                // If provided an incorrect CID, replace it by the placeholder
                if (
                    Object.prototype.hasOwnProperty.call(activatedRouteSnapshot, 'params') &&
                    Object.prototype.hasOwnProperty.call(activatedRouteSnapshot.params, 'cid')
                ) {
                    updatedReturnUrl = routerStateSnapshot.url.replace(
                        activatedRouteSnapshot.params.cid,
                        encodeURI('{{cid}}'),
                    );
                }

                // let's go to contract selector page
                this.routerService.navigate([ContractPortfolioResolver.CONTRACT_SELECTOR_PATH], {
                    queryParams: Object.assign(
                        { returnUrl: updatedReturnUrl, force: this.force },
                        activatedRouteSnapshot.data,
                    ),
                });

                return of(false);
            }),
        );
    }
}
